import React, { FC, useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './customYAxis.css';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { IGmbHistoryScore } from '../../shared/interfaces/IGmbHistoryScore';

export interface ChartData {
  month: string;
  score: number;
  reviewCount: number;
}

interface Props {
  mode: FilterMode;
  scores: IGmbHistoryScore[];
}

const ChartComponent: FC<Props> = ({ mode, scores }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [chartData, setChartData] = useState<ChartData[]>([]);

  const getScores = (scoreEntries: IGmbHistoryScore[]): ChartData[] => {
    return scoreEntries.map((score) => {
      return {
        month: format(new Date(score.scrapedAt), 'MMM'),
        score: parseInt(score.score.averageRating.toFixed(1)),
        reviewCount: score.score.totalReviewCount,
      };
    });
  };

  const getScoresYEAR = (scoreEntries: IGmbHistoryScore[]): ChartData[] => {
    return scoreEntries.map((score, index) => {
      if (index === 0) {
        return {
          month: format(new Date(score.scrapedAt), 'MMM yy'),
          score: Math.round(score.score.averageRating * 10) / 10,
          reviewCount: score.score.totalReviewCount,
        };
      } else if (index === scoreEntries.length - 1) {
        return {
          month: format(new Date(score.scrapedAt), 'MMM yy'),
          score: Math.round(score.score.averageRating * 10) / 10,
          reviewCount: score.score.totalReviewCount,
        };
      } else {
        return {
          month: format(new Date(score.scrapedAt), 'MMM'),
          score: Math.round(score.score.averageRating * 10) / 10,
          reviewCount: score.score.totalReviewCount,
        };
      }
    });
  };

  useEffect(() => {
    if (!scores) return;
    const getScoresForMode = (mode: FilterMode) => {
      switch (mode) {
        case 'YTD':
          return getScores(scores);
        case 'YEAR':
          return getScoresYEAR(scores);
        default:
          return [];
      }
    };

    setChartData(getScoresForMode(mode));
  }, [scores, mode]);

  const renderCustomAxisTick = ({ x, y, payload }: any) => {
    const stars = [...Array(payload.value)].map((_, index) => (
      <svg
        x={index + 1 >= 1 ? x - 24 * (index + 1) : x - 24}
        y={y - 12}
        width={24}
        height={24}
        viewBox="0 0 24 24"
        fill={theme.palette.secondary.main}
        key={index}
      >
        <g>
          <path
            d="M0,0h24v24H0V0z"
            fill="none"
          />
          <path
            d="M0,0h24v24H0V0z"
            fill="none"
          />
        </g>
        <g>
          <path d="M12,17.27L18.18,21l-1.64-7.03L22,9.24l-7.19-0.61L12,2L9.19,8.63L2,9.24l5.46,4.73L5.82,21L12,17.27z" />
        </g>
      </svg>
    ));

    return <>{stars}</>;
  };

  return (
    <ResponsiveContainer width="100%">
      <ComposedChart
        margin={{ top: 25, right: 5, bottom: 5, left: parseInt(theme.spacing(13)) }}
        data={chartData}
      >
        <CartesianGrid
          stroke={theme.palette.primary.main}
          horizontal={false}
          strokeDasharray={1}
        />
        <XAxis
          dataKey="month"
          tickFormatter={(value) => {
            return t(value);
          }}
        />
        <YAxis
          yAxisId="left"
          type="number"
          domain={[1, 5]}
          dataKey="score"
          tick={renderCustomAxisTick}
        />
        <YAxis
          yAxisId="right"
          type="number"
          domain={[0, 130]}
          allowDecimals={false}
          tickCount={15}
          dataKey="reviewCount"
          orientation="right"
          className="customYAxis"
        />
        <Bar
          dataKey="reviewCount"
          yAxisId="right"
          barSize={40}
          fill={theme.palette.secondary.main}
          shape={
            <Rectangle
              radius={[
                theme.shape.borderRadius as number,
                theme.shape.borderRadius as number,
                0,
                0,
              ]}
            />
          }
        >
          <LabelList
            dataKey="reviewCount"
            position="insideBottom"
          />
        </Bar>
        <Line
          type="linear"
          yAxisId="left"
          dataKey="score"
          stroke={theme.palette.primary.main}
        >
          <LabelList
            dataKey="score"
            position="top"
          />
        </Line>
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: `${theme.shape.borderRadius}px`,
                    p: theme.spacing(1),
                  }}
                >
                  <Typography variant="body2">
                    {t('nav_reviews')}: {payload[0].value}
                  </Typography>
                  <Typography variant="body2">
                    {t('score')}: {payload[1].value}
                  </Typography>
                </Box>
              );
            }
          }}
          cursor={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default ChartComponent;
